import type { TilePos } from './MapGen';

/**
 * Chunk-stitched map generation (MAPGEN_PLAN.md) — template format, parsing,
 * transforms, validation, and the signature index the assembler picks from.
 *
 * A floor is a coarse grid of CELL×CELL-tile cells. Every open cell edge uses
 * the same standard aperture: an 8-tile floor opening on the boundary row/col
 * at [APERTURE_LO, APERTURE_HI] (per 24-tile edge segment). Any two chunks
 * with facing open edges therefore seam perfectly — connectivity is by
 * construction, provided each template's floor is internally connected
 * (validated below).
 *
 * Templates are authored ASCII in `chunkLibrary.ts`:
 *   `#` wall · `.` floor · `e` event-anchor (floor) · `*` room center (floor,
 *   exactly one — rooms[].cx/cy must be guaranteed floor for consumers).
 *
 * Rotation + mirror are free variety (procedural tiles, no baked art): each
 * template expands to 8 variants at module load, indexed by open-edge mask.
 */

export const CELL = 24;
/** Standard aperture strip, inclusive, per 24-tile edge segment. */
export const APERTURE_LO = 8;
export const APERTURE_HI = 15;

/**
 * `entry` — entrance chamber (cap-shaped, never used elsewhere);
 * `cap` — dead-end pocket room (branch ends);
 * `hall`/`room` — connective tissue vs chambers;
 * `grand` — multi-cell (2×2 arena, 3×3 sanctum giant) summon/boss room,
 * authored with ALL edge segments open; the assembler seals unused
 * apertures (seal-down).
 */
export type ChunkKind = 'entry' | 'cap' | 'hall' | 'room' | 'grand';

export interface ChunkTemplate {
  id: string;
  kind: ChunkKind;
  /** Cells per side (square): 1 → 24×24 rows, 2 → 48×48, 3 → 72×72. */
  size: 1 | 2 | 3;
  rows: string[];
}

/** Edge-mask bits (size-1 chunks). */
export const N = 1;
export const E = 2;
export const S = 4;
export const W = 8;

export interface ChunkVariant {
  templateId: string;
  kind: ChunkKind;
  size: 1 | 2 | 3;
  /** Tiles per side. */
  n: number;
  /** 0 = wall, 1 = floor, chunk-local, row-major. */
  tiles: Uint8Array;
  /** Room center (the `*` glyph), chunk-local. */
  center: TilePos;
  /** Event anchors (`e` glyphs), chunk-local. */
  anchors: TilePos[];
  /** Floor bounding box, chunk-local. */
  bbox: { x: number; y: number; w: number; h: number };
  /** Open-edge mask (size-1). Size-2 grands are all-open by contract. */
  mask: number;
}

const GLYPHS = new Set(['#', '.', 'e', '*']);

// ---------------------------------------------------------------------------
// Parsing & transforms

function parseRows(t: ChunkTemplate): ChunkVariant {
  const n = t.size * CELL;
  const tiles = new Uint8Array(n * n);
  const anchors: TilePos[] = [];
  let center: TilePos | null = null;
  for (let y = 0; y < n; y++) {
    const row = t.rows[y] ?? '';
    for (let x = 0; x < n; x++) {
      const g = row[x] ?? '#';
      if (g === '#') continue;
      tiles[y * n + x] = 1;
      if (g === 'e') anchors.push({ x, y });
      if (g === '*') center = { x, y };
    }
  }
  const v: ChunkVariant = {
    templateId: t.id,
    kind: t.kind,
    size: t.size,
    n,
    tiles,
    center: center ?? { x: 0, y: 0 },
    anchors,
    bbox: floorBBox(tiles, n),
    mask: 0,
  };
  v.mask = edgeMask(v);
  return v;
}

function floorBBox(tiles: Uint8Array, n: number): { x: number; y: number; w: number; h: number } {
  let minX = n;
  let minY = n;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (tiles[y * n + x] !== 1) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return { x: 0, y: 0, w: 0, h: 0 };
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/** Open-edge mask from tile data: an edge is open iff its aperture strip is floor. */
function edgeMask(v: ChunkVariant): number {
  let mask = 0;
  if (stripOpen(v, 'n', 0)) mask |= N;
  if (stripOpen(v, 'e', 0)) mask |= E;
  if (stripOpen(v, 's', 0)) mask |= S;
  if (stripOpen(v, 'w', 0)) mask |= W;
  return mask;
}

/**
 * Aperture strip state on an edge segment (size-2 chunks have 2 segments per
 * side, seg ∈ {0, 1}). Returns true iff the whole 8-tile strip is floor.
 */
export function stripOpen(v: ChunkVariant, side: 'n' | 'e' | 's' | 'w', seg: number, depth = 0): boolean {
  for (let i = APERTURE_LO; i <= APERTURE_HI; i++) {
    const u = seg * CELL + i;
    let x: number;
    let y: number;
    if (side === 'n') [x, y] = [u, depth];
    else if (side === 's') [x, y] = [u, v.n - 1 - depth];
    else if (side === 'w') [x, y] = [depth, u];
    else [x, y] = [v.n - 1 - depth, u];
    if (v.tiles[y * v.n + x] !== 1) return false;
  }
  return true;
}

/** Rotate 90° clockwise `rot` times, then mirror horizontally if `mirror`. */
export function transformVariant(v: ChunkVariant, rot: number, mirror: boolean): ChunkVariant {
  const n = v.n;
  const map = (x: number, y: number): [number, number] => {
    for (let r = 0; r < rot; r++) [x, y] = [n - 1 - y, x];
    if (mirror) x = n - 1 - x;
    return [x, y];
  };
  const tiles = new Uint8Array(n * n);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (v.tiles[y * n + x] !== 1) continue;
      const [tx, ty] = map(x, y);
      tiles[ty * n + tx] = 1;
    }
  }
  const [ccx, ccy] = map(v.center.x, v.center.y);
  const out: ChunkVariant = {
    ...v,
    tiles,
    center: { x: ccx, y: ccy },
    anchors: v.anchors.map((a) => {
      const [ax, ay] = map(a.x, a.y);
      return { x: ax, y: ay };
    }),
    bbox: floorBBox(tiles, n),
    mask: 0,
  };
  out.mask = edgeMask(out);
  return out;
}

// ---------------------------------------------------------------------------
// Validation (the authoring safety net — see ChunkDefs.test.ts)

export function validateTemplate(t: ChunkTemplate): string[] {
  const errs: string[] = [];
  const n = t.size * CELL;
  if (t.rows.length !== n) errs.push(`${t.id}: ${t.rows.length} rows, want ${n}`);
  t.rows.forEach((row, y) => {
    if (row.length !== n) errs.push(`${t.id} row ${y}: length ${row.length}, want ${n}`);
    for (const g of row) if (!GLYPHS.has(g)) errs.push(`${t.id} row ${y}: bad glyph '${g}'`);
  });
  if (errs.length > 0) return errs; // shape errors make the rest noise

  const stars = t.rows.join('').split('').filter((g) => g === '*').length;
  if (stars !== 1) errs.push(`${t.id}: ${stars} '*' centers, want exactly 1`);

  const v = parseRows(t);

  // Edge contract: per segment, the aperture strip is uniformly floor or
  // wall; open strips are floor 2 deep (no seam pinches); every non-aperture
  // boundary tile is wall.
  const sides = ['n', 'e', 's', 'w'] as const;
  let openEdges = 0;
  for (const side of sides) {
    for (let seg = 0; seg < t.size; seg++) {
      const open = stripOpen(v, side, seg);
      if (open) {
        openEdges++;
        if (!stripOpen(v, side, seg, 1)) {
          errs.push(`${t.id}: open ${side}${seg} aperture not floor at depth 1`);
        }
      } else {
        // Closed must be UNIFORM wall (a half-open strip would half-seam).
        for (let i = APERTURE_LO; i <= APERTURE_HI; i++) {
          const u = seg * CELL + i;
          const [x, y] = side === 'n' ? [u, 0] : side === 's' ? [u, n - 1]
            : side === 'w' ? [0, u] : [n - 1, u];
          if (v.tiles[y * n + x] === 1) {
            errs.push(`${t.id}: ${side}${seg} aperture strip is mixed floor/wall`);
            break;
          }
        }
      }
    }
  }
  for (let u = 0; u < n; u++) {
    const inStrip = u % CELL >= APERTURE_LO && u % CELL <= APERTURE_HI;
    if (inStrip) continue;
    for (const [x, y] of [[u, 0], [u, n - 1], [0, u], [n - 1, u]] as const) {
      if (v.tiles[y * n + x] === 1) {
        errs.push(`${t.id}: floor outside aperture on boundary at (${x},${y})`);
      }
    }
  }

  // Kind constraints.
  if ((t.kind === 'entry' || t.kind === 'cap') && openEdges !== 1) {
    errs.push(`${t.id}: kind ${t.kind} wants exactly 1 open edge, has ${openEdges}`);
  }
  if ((t.kind === 'hall' || t.kind === 'room') && openEdges < 2) {
    errs.push(`${t.id}: kind ${t.kind} wants ≥2 open edges, has ${openEdges}`);
  }
  if (t.kind === 'grand') {
    if (t.size < 2) errs.push(`${t.id}: grand must be multi-cell`);
    if (openEdges !== 4 * t.size) {
      errs.push(`${t.id}: grand wants all ${4 * t.size} segments open, has ${openEdges}`);
    }
    // Mirror of the boss-arena invariant (rect ≥24×20, ≥85% open).
    const { bbox } = v;
    if (bbox.w < 24 || bbox.h < 20) errs.push(`${t.id}: arena bbox ${bbox.w}×${bbox.h} < 24×20`);
    let open = 0;
    for (let y = bbox.y; y < bbox.y + bbox.h; y++) {
      for (let x = bbox.x; x < bbox.x + bbox.w; x++) {
        if (v.tiles[y * n + x] === 1) open++;
      }
    }
    if (open / (bbox.w * bbox.h) < 0.85) {
      errs.push(`${t.id}: arena openness ${(open / (bbox.w * bbox.h)).toFixed(2)} < 0.85`);
    }
  }

  // Full internal connectivity: every floor tile reachable from the center.
  // (With per-template connectivity + aligned apertures, whole-map
  // connectivity is by construction.)
  const seen = new Uint8Array(n * n);
  const queue = [v.center.y * n + v.center.x];
  seen[queue[0]!] = 1;
  let reached = 0;
  while (queue.length > 0) {
    const cur = queue.pop()!;
    reached++;
    const cx = cur % n;
    const cy = (cur / n) | 0;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const x = cx + dx;
      const y = cy + dy;
      if (x < 0 || x >= n || y < 0 || y >= n) continue;
      const i = y * n + x;
      if (v.tiles[i] === 1 && seen[i] === 0) {
        seen[i] = 1;
        queue.push(i);
      }
    }
  }
  let floorCount = 0;
  for (let i = 0; i < n * n; i++) if (v.tiles[i] === 1) floorCount++;
  if (v.tiles[v.center.y * n + v.center.x] !== 1) errs.push(`${t.id}: center not on floor`);
  else if (reached !== floorCount) {
    errs.push(`${t.id}: floor not connected (${reached}/${floorCount} reachable from center)`);
  }

  return errs;
}

// ---------------------------------------------------------------------------
// Variant index

export interface VariantIndex {
  /** Non-entry size-1 variants by open-edge mask. */
  byMask: Map<number, ChunkVariant[]>;
  /** Entry variants by (single-edge) mask. */
  entryByMask: Map<number, ChunkVariant[]>;
  /** Grand (2×2) variants. */
  grands: ChunkVariant[];
}

/**
 * Expand templates × 8 orientations into a mask index. Deterministic order
 * (library order × rot 0–3 × mirror) — the assembler's picks draw from these
 * lists, so this order is part of the seeded-layout contract.
 */
export function buildVariantIndex(templates: readonly ChunkTemplate[]): VariantIndex {
  const byMask = new Map<number, ChunkVariant[]>();
  const entryByMask = new Map<number, ChunkVariant[]>();
  const grands: ChunkVariant[] = [];
  for (const t of templates) {
    const base = parseRows(t);
    for (const mirror of [false, true]) {
      for (let rot = 0; rot < 4; rot++) {
        const v = transformVariant(base, rot, mirror);
        if (t.kind === 'grand') {
          grands.push(v);
          continue;
        }
        const index = t.kind === 'entry' ? entryByMask : byMask;
        const list = index.get(v.mask);
        if (list) list.push(v);
        else index.set(v.mask, [v]);
      }
    }
  }
  return { byMask, entryByMask, grands };
}
